import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  canAccessKitchenDisplay,
  canAccessStoreDashboard,
  canAccessStorePOS,
  canViewStoreInvoices,
  useStoreStaffRoles,
} from '../utils/storePermissions';

const ProtectedStoreRoute = ({ 
  children, 
  storeId, 
  storeSlug,
  requiredPermission = 'dashboard', // 'dashboard', 'pos', 'invoices', 'kitchen'
  fallbackPath = '/' 
}) => {
  const location = useLocation();
  const { user } = useAuth();

  const isGlobalAdmin = Array.isArray(user?.roles) && (
    user.roles.includes('tote') || user.roles.includes('admin')
  );
  
  // If storeSlug is provided, get the store ID
  const actualStoreId = React.useMemo(() => {
    if (storeId && storeId !== storeSlug) return storeId;
    return storeSlug; // Will be resolved in the query
  }, [storeId, storeSlug]);
  
  const { data: staffData, isLoading, error } = useStoreStaffRoles(actualStoreId);

  // If user is not logged in, redirect to login
  if (!user) {
    return (
      <Navigate 
        to={`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`} 
        replace 
      />
    );
  }

  // Admin global: acceso completo a rutas de tienda sin depender de store_staff
  if (isGlobalAdmin) {
    return children;
  }

  // Show loading spinner while checking permissions
  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  // If there's an error or user is not staff, redirect to store public page
  if (error || !staffData) {
    const redirectSlug = storeSlug || actualStoreId;
    return <Navigate to={`/store/${redirectSlug}`} replace />;
  }

  // Check specific permissions
  let hasPermission = false;
  
  switch (requiredPermission) {
    case 'dashboard':
      hasPermission = canAccessStoreDashboard(staffData);
      break;
    case 'pos':
      hasPermission = canAccessStorePOS(staffData);
      break;
    case 'invoices':
      hasPermission = canViewStoreInvoices(staffData);
      break;
    case 'kitchen':
      hasPermission = canAccessKitchenDisplay(staffData);
      break;
    default:
      hasPermission = false;
  }

  if (!hasPermission) {
    // If user is staff but doesn't have permission, redirect to store public page
    const redirectSlug = storeSlug || actualStoreId;
    return <Navigate to={`/store/${redirectSlug}`} replace />;
  }

  // User has permission, render children
  return children;
};

export default ProtectedStoreRoute;
